import React, { useEffect, useReducer, useRef } from 'react';
import {
  Button, ButtonToolbar, Form, FormInstance, Loader, SelectPicker,
} from 'rsuite';
import { ItemDataType } from 'rsuite/cjs/internals/types';
import axios from 'axios';
import { AllegroImpliedWarranty } from '../../../../../api/allegro/impliedwarranty/impliedWarrantyTypes';
import {
  createAllegroImpliedWarranty,
  getAllegroImpliedWarranty,
  updateAllegroImpliedWarranty,
} from '../../../../../api/allegro/impliedwarranty/impliedWarranty';
import { showErrorMessage, showSuccessMessage } from '../../../../../utils/message';
import { ALL_COUNTRY_CODES } from '../../../../../utils/countries';
import Textarea from '../../../../Textarea';
import { Edit, getInitialState, reducer } from './impliedWarrantyState';
import validationModel from './impliedWarrantyValidation';
import mapRequest from './impliedWarrantyRequest';

const PERIOD_OPTIONS: ItemDataType[] = Array.from(
  { length: 19 },
  (_, index) => {
    const years = index + 2;
    return { value: `P${years}Y`, label: `${years} years` };
  },
);

const COUNTRY_CODES: ItemDataType[] = ALL_COUNTRY_CODES.map((countryCode) => ({
  value: countryCode,
  label: countryCode,
}));

interface Props {
  marketplaceCode: string;
  impliedWarranty?: AllegroImpliedWarranty;
  onCancel: () => void;
  onSaved: () => void;
}

export default function EshopSetupAllegroImpliedWarrantyEdit({
  marketplaceCode,
  impliedWarranty,
  onCancel,
  onSaved,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, impliedWarranty, getInitialState);
  const formRef = useRef<FormInstance>(null);

  async function fetchImpliedWarranty(id: string, signal: AbortSignal) {
    dispatch({ type: 'status/set', payload: 'loading' });

    try {
      const fullImpliedWarranty = await getAllegroImpliedWarranty(marketplaceCode, id, signal);
      dispatch({ type: 'impliedWarranty/set', payload: fullImpliedWarranty });
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      if (axios.isAxiosError(error)) {
        showErrorMessage(JSON.stringify(error.response?.data));
      }

      onCancel();
    }
  }

  useEffect(() => {
    if (!impliedWarranty) {
      return undefined;
    }

    const controller = new AbortController();
    fetchImpliedWarranty(impliedWarranty.id, controller.signal).then();

    return () => {
      controller.abort();
    };
  }, [impliedWarranty?.id, marketplaceCode]);

  async function saveImpliedWarranty() {
    dispatch({ type: 'status/set', payload: 'saving' });

    try {
      const request = mapRequest(state.edit);

      if (impliedWarranty) {
        await updateAllegroImpliedWarranty(marketplaceCode, impliedWarranty.id, request);
      } else {
        await createAllegroImpliedWarranty(marketplaceCode, request);
      }

      showSuccessMessage('Complaints terms successfully saved.');
      onSaved();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        showErrorMessage(JSON.stringify(error.response?.data));
      }
    } finally {
      dispatch({ type: 'status/set', payload: 'ready' });
    }
  }

  function handleSave() {
    if (!formRef.current?.check()) {
      return;
    }

    saveImpliedWarranty().then();
  }

  if (state.status === 'loading') {
    return <Loader content="Loading..." />;
  }

  return (
    <Form
      ref={formRef}
      fluid
      formValue={state.edit}
      onChange={(edit) => dispatch({ type: 'edit/set', payload: edit as Edit })}
      model={validationModel}
      layout="vertical"
    >
      <Form.Group>
        <Form.ControlLabel>Label</Form.ControlLabel>
        <Form.Control name="label" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Individual period</Form.ControlLabel>
        <Form.Control
          name="individualPeriod"
          accepter={SelectPicker}
          data={PERIOD_OPTIONS}
          cleanable
          searchable={false}
          block
        />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Corporate period</Form.ControlLabel>
        <Form.Control
          name="corporatePeriod"
          accepter={SelectPicker}
          data={PERIOD_OPTIONS}
          cleanable
          searchable={false}
          block
        />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Name</Form.ControlLabel>
        <Form.Control name="name" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Street</Form.ControlLabel>
        <Form.Control name="street" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Postal code</Form.ControlLabel>
        <Form.Control name="postCode" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>City</Form.ControlLabel>
        <Form.Control name="city" type="text" />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Country code</Form.ControlLabel>
        <Form.Control
          name="countryCode"
          accepter={SelectPicker}
          data={COUNTRY_CODES}
          cleanable={false}
          searchable
          block
        />
      </Form.Group>
      <Form.Group>
        <Form.ControlLabel>Description</Form.ControlLabel>
        <Form.Control
          name="description"
          accepter={Textarea}
        />
      </Form.Group>
      <ButtonToolbar>
        <Button
          appearance="primary"
          onClick={() => handleSave()}
          loading={state.status === 'saving'}
          type="submit"
        >
          Save
        </Button>
        <Button appearance="ghost" onClick={() => onCancel()}>
          Cancel
        </Button>
      </ButtonToolbar>
    </Form>
  );
}

EshopSetupAllegroImpliedWarrantyEdit.defaultProps = {
  impliedWarranty: undefined,
};
